import React from 'react'
import { cn } from '../UI/cn'

interface UnderlineTabItem<K extends string> {
  key: K
  title: string
  /** Optional count rendered as a Basil 50 / Basil 700 pill next to the label. */
  count?: number | null
}

interface UnderlineTabsProps<K extends string> {
  tabs: ReadonlyArray<UnderlineTabItem<K>>
  activeKey: NoInfer<K>
  onChange: (key: NoInfer<K>) => void
  /** Unique-per-page prefix for tab/panel id wiring. Pair it with the
   *  matching `<TabPanel idPrefix={...}>` so screen readers can navigate
   *  from a tab to the panel it controls. */
  idPrefix: string
  className?: string
  'aria-label'?: string
  /** `md` (default) is the full-height tab; `sm` is the compact variant
   *  (shorter, `text-sm`) that matches the DS2026 `sm` controls — same idea as
   *  PillTabs' `size="sm"`. */
  size?: 'md' | 'sm'
}

const tabIdFor = (prefix: string, key: string): string => `${prefix}-tab-${key}`
const panelIdFor = (prefix: string, key: string): string =>
  `${prefix}-panel-${key}`

/**
 * Horizontal underline tabs with optional count badges, matching the DS2026
 * Figma `HorizontalTabsBase` "With badge" variant. Active tab gets a 2px
 * Basil 700 underline; inactive tabs use neutral-500 text. The container has
 * a Gray/200 border-bottom that the active underline crosses.
 *
 * Wraps the same WAI-ARIA `role="tablist" / role="tab"` + arrow-key roving
 * focus pattern as PillTabs so a/11y stays in lockstep.
 */
export function UnderlineTabs<K extends string>({
  tabs,
  activeKey,
  onChange,
  idPrefix,
  className,
  'aria-label': ariaLabel,
  size = 'md',
}: UnderlineTabsProps<K>) {
  const isSm = size === 'sm'
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft')
      nextIndex = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = tabs.length - 1
    if (nextIndex === null) return
    event.preventDefault()
    const nextTab = tabs[nextIndex]
    onChange(nextTab.key)
    const container = event.currentTarget.parentElement
    container
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      [nextIndex]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex items-end gap-2 border-b border-gray-200', className)}
    >
      {tabs.map((tab, index) => {
        const isActive = activeKey === tab.key
        return (
          <button
            key={tab.key}
            id={tabIdFor(idPrefix, tab.key)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelIdFor(idPrefix, tab.key)}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'flex items-center justify-center border-b-2 font-medium tracking-[-0.2px] transition-colors',
              isSm
                ? 'h-9 gap-1.5 px-2.5 text-sm'
                : 'h-14 gap-2.5 px-4 text-base',
              isActive
                ? 'border-brand-active text-brand-active'
                : 'border-transparent text-gray-500 hover:text-neutral-900'
            )}
          >
            <span>{tab.title}</span>
            {tab.count != null && (
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full bg-basil-50 font-medium text-brand-active',
                  isSm ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm'
                )}
                style={{ lineHeight: isSm ? '16px' : '20px' }}
              >
                {tab.count.toLocaleString()}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
